<template>
  <page-header title="Users">
    <router-link
      to="/user"
      class="btn-primary ml-auto h-10"
    >
      <PlusCircleIcon class="size-5 -ml-1 mr-2" />
      Add
    </router-link>
  </page-header>

  <grid-items-empty v-if="isEmpty && search === null" :link="`/user`" />

  <template v-else>
    <div class="flex justify-between mb-4">
      <input-search v-model.trim="search" class="max-w-lg grow" />

      <reload-button class="ml-4" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :data-handler="getUsers"
      :columns="columns"
      :row-class="rowClass"
      :per-page="15"
      @enable="onEnable"
      @disable="onDisable"
      @add-time="onAddTime"
      @remove-time="onRemoveTime"
      @set-time="onSetTime"
    />
  </template>
</template>

<script setup>
import { ref, markRaw } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import GridItemsEmpty from '../components/grid/GridItemsEmpty.vue';
import UserListActions from '../components/UserListActions.vue';
import UserIcon from '../components/UserIcon.vue';
import UserCanRunPrograms from '../components/UserCanRunPrograms.vue';
import UserIsDeleted from '../components/UserIsDeleted.vue';
import Grid from '../components/grid/Grid.vue';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { useRouter } from 'vue-router';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';
import { formatDate, formatBytes } from '../utils/formatters';

const gridRef = ref(null);
const isEmpty = ref(false);
const router = useRouter();
const toast = useToast();

function rowClass(_row, index) {
  if (_row.tier === 'admin') {
    return 'bg-primary-100/50';
  }
  if (_row.tier === 'seo') {
    return 'bg-fuchsia-100/50';
  }
  if (_row.tier === 'staff') {
    return 'bg-sky-100/50';
  }
  return (index % 2 ? 'bg-zinc-200/10' : '') + (_row.deletedAt ? ' [&>td:not(:last-child)]:!opacity-30' : ''); // stripes class for row
}

function reloadGrid(updateQuery = {}) {
  if (gridRef.value?.reload) {
    gridRef.value.reload(updateQuery);
  }
}

const search = useRouteQuery('search', null);

watchDebounced(
  search,
  () => {
    reloadGrid({ offset: 0 });
  },
  { debounce: 500, maxWait: 1000 }
);

function getUrl({ limit, offset, sort, direction }) {
  const data = { limit, offset, order: sort, direction: direction?.toLowerCase(), search: search.value ?? '' };

  const searchParams = new URLSearchParams(data);

  return `${apiDomain}/api/user/?${searchParams}`;
}

async function getUsers(args) {
  const { limit, offset, sort, direction } = args;
  const { data } = await useFetch(getUrl({ limit, offset, sort, direction })).get().json();

  isEmpty.value = !data.value.total;

  return {
    total: data.value.total,
    rows: data.value.users
  };
}

const columns = [
  {
    title: '#',
    prop: 'id',
    class: 'text-xs font-mono',
    titleClass: '', // class for title cell
    sortable: true
  },
  {
    title: 'Name',
    prop: 'name',
    component: markRaw(UserIcon),
    class: 'py-0!',
    titleClass: 'pl-14!',
    click: row => router.push(`/user/${row.id}`),
    sortable: true
  },
  {
    title: 'Email',
    prop: 'email',
    sortable: true
  },
  {
    title: 'IP',
    prop: 'registrationIp'
  },
  {
    title: 'CreatedAt',
    prop: 'createdAt',
    value: row => formatDate(row.createdAt),
    sortable: true
  },
  {
    title: 'Logged At',
    prop: 'lastActivityAt',
    value: row => formatDate(row.lastActivityAt),
    sortable: true
  },
  {
    title: 'Storage usage',
    prop: 'storageUsage',
    value: row => formatBytes(row.storageUsage),
    class: 'text-xs font-mono',
    titleClass: 'text-xs font-mono',
    sortable: true
  },
  {
    title: 'Is enabled',
    component: markRaw(UserIsDeleted)
  },
  {
    title: 'Can run programs',
    component: markRaw(UserCanRunPrograms)
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(UserListActions)
  }
];

async function onEnable(row) {
  const confirm = await Modal.confirm({
    title: 'Enable User',
    body: `Enable the user "${row.name}"?`,
    okButton: 'Enable',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/user/enable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('User enabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error enabling user');
}

async function onDisable(row) {
  const confirm = await Modal.confirm({
    title: 'Disable User',
    body: `Disable the user "${row.name}"?`,
    okButton: 'Disable',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/user/disable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('User disabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error disabling user');
}

async function onAddTime(row) {
  const { data } = await useFetch(`${apiDomain}/api/user/add-time/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('User recieved +10 hours');
    reloadGrid();
    return;
  }

  toast.error('Error adding +10 hours');
}

async function onRemoveTime(row) {
  const { data } = await useFetch(`${apiDomain}/api/user/remove-time/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('User time completely removed');
    reloadGrid();
    return;
  }

  toast.error('Error removing time');
}

async function onSetTime(row) {
  const currentMinutes = Math.round((row.timeLeftSeconds ?? 0) / 60);
  const input = window.prompt('Set time (minutes):', String(currentMinutes));
  if (input === null) return;

  const minutes = Number(input);
  if (!Number.isFinite(minutes) || minutes < 0) {
    toast.error('Enter a non‑negative number of minutes');
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/user/set-time/${row.id}`).post({ minutes }).json();

  if (data.value?.success) {
    toast.success(`Time set to ${minutes} minutes`);
    reloadGrid();
    return;
  }

  toast.error('Error setting time');
}
</script>
